<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header :title="viewTitle" :count="viewCount" icon="fa-file-certificate"/>

            <div class="columns mt-6">
                <div class="column is-list">

                    <div v-if="loading" class="has-text-white">
                        <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading certificates...
                    </div>

                    <!-- Available Applications (/reports/available): applications and forms
                         the student's courses and conferences offer that they haven't
                         started. Ready ones first; the rest say what stands in the way. -->
                    <div v-else-if="currentView === 'available'">
                        <div v-if="availableLoading" class="has-text-white">
                            <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading available applications...
                        </div>
                        <empty-state v-else-if="availableApplications.length === 0" icon="fa-info-circle"
                                     text="Nothing new to apply for. Applications and forms offered by your courses and conferences appear here until you start them."/>
                        <template v-else>
                            <template v-for="(section, sectionIndex) in availableSections">
                                <section-header :key="'ah-' + section.key" :title="section.title" :icon="section.icon" :count="section.items.length" :class="['mb-4', sectionIndex === 0 ? 'mt-2' : 'mt-6 pt-3']"/>
                                <div v-for="item in section.items" :key="`${item.type}-${item.enrollmentId || item.registrationId}-${item.id}`" class="info-box is-card mb-4">
                                    <div class="box-main">
                                        <div class="box-icon is-hidden-touch"><i :class="['fad', 'fa-2x', item.application.type === 'form' ? 'fa-file-invoice' : 'fa-file-certificate', availableMeta(item).iconClass]"></i></div>
                                        <div class="box-body">
                                            <div class="card-title">{{ certTitle(item) }}</div>
                                            <div class="card-meta" v-if="item.certification">{{ item.certification.title }}</div>
                                            <div class="card-chips mt-2">
                                                <status-chip :label="availableMeta(item).chip" :type="availableMeta(item).chipType" :icon="availableMeta(item).chipIcon"/>
                                                <status-chip v-if="item.application.type === 'form'" label="Form" type="neutral" icon="fa-file-invoice"/>
                                                <status-chip v-if="item.isRequired" label="Required" type="warning" icon="fa-exclamation-triangle"/>
                                            </div>
                                            <div class="card-meta mt-3">
                                                <div v-if="item.course"><span class="meta-label">Course</span>{{ item.course.title }}</div>
                                                <div v-if="item.conference"><span class="meta-label">Conference</span>{{ item.conference.title }}</div>
                                                <div v-if="item.application.organization"><span class="meta-label">Provider</span>{{ item.application.organization.title }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="box-alert" v-if="availableNote(item)">
                                        <i class="fas fa-fw fa-circle-info mr-2"></i>{{ availableNote(item) }}
                                    </div>
                                    <div class="box-actions">
                                        <div class="box-buttons">
                                            <b-button v-if="item.type === 'enrollment'" type="is-primary" outlined size="is-small" tag="router-link" :to="'/course/' + item.enrollmentId"><i class="fas fa-chalkboard-teacher mr-2"></i>View Course</b-button>
                                            <b-button v-if="item.type === 'registration'" type="is-primary" outlined size="is-small" tag="router-link" :to="'/conference/' + item.registrationId"><i class="fas fa-presentation mr-2"></i>View Conference</b-button>
                                            <b-button v-if="availableStatus(item) === 'cert_off'" type="is-primary" size="is-small" tag="router-link" :to="{name: 'account', params: {section: 'certs'}}"><i class="far fa-toggle-on mr-2"></i>Turn On</b-button>
                                            <b-button v-else type="is-primary" :outlined="!availableMeta(item).ready" size="is-small" tag="router-link" :to="availableRoute(item)"><i class="fas fa-arrow-right mr-2"></i>{{ item.application.type === 'form' ? 'View Form' : 'View Application' }}</b-button>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </div>

                    <!-- In Progress Applications (/reports/applications) -->
                    <div v-else-if="currentView === 'progress'">

                        <empty-state v-if="inProgressCertificates.length === 0" icon="fa-info-circle"
                                     text="You have no applications in progress. To earn a certification, look for available applications in a course enrollment or conference registration."/>
                        <div v-else>
                            <div v-for="certificate in inProgressCertificates" :key="`${certificate.type}-${certificate.id}`" class="info-box is-card mb-4">
                                <div class="box-main">
                                    <div class="box-icon is-hidden-touch"><i :class="['fad', 'fa-2x', stateMeta(certificate).icon, stateMeta(certificate).iconClass]"></i></div>
                                    <div class="box-body">
                                        <div class="card-title">{{ certTitle(certificate) }}</div>
                                        <div class="card-meta" v-if="certificate.certification">{{ certificate.certification.title }}</div>
                                        <!-- The alert line carries the state when there is one; the
                                             chip only speaks for plain in-progress rows -->
                                        <div class="card-chips mt-2" v-if="!stateMeta(certificate).note">
                                            <status-chip :label="stateMeta(certificate).chip" :type="stateMeta(certificate).chipType" :icon="stateMeta(certificate).chipIcon"/>
                                            <status-chip :label="'Step ' + (certificate.progress || 1)" type="neutral"/>
                                        </div>
                                        <div class="card-meta mt-3">
                                            <div v-if="certificate.course"><span class="meta-label">Course</span>{{ certificate.course.title }}</div>
                                            <div v-if="certificate.conference"><span class="meta-label">Conference</span>{{ certificate.conference.title }}</div>
                                            <div v-if="certificate.application.organization"><span class="meta-label">Provider</span>{{ certificate.application.organization.title }}</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="box-alert" v-if="stateMeta(certificate).note">
                                    <i class="fas fa-fw fa-circle-info mr-2"></i>{{ stateMeta(certificate).note }}
                                    <!-- The reviewer's own words, when they left any -->
                                    <div class="review-message" v-if="certificate.reviewMessage && ['changes_requested', 'rejected'].includes(certificate.state)">&ldquo;{{ certificate.reviewMessage }}&rdquo;</div>
                                </div>
                                <div class="box-actions">
                                    <div class="box-lead box-info" v-if="certificate.updatedAt"><i class="far fa-fw fa-clock mr-2"></i>Last updated {{ certificate.updatedAt | timeAgo | lowercase }}</div>
                                    <div class="box-buttons">
                                        <b-button v-if="certificate.type === 'enrollment' && certificate.course" type="is-primary" outlined size="is-small" tag="router-link" :to="'/course/' + certificate.enrollmentId"><i class="fas fa-chalkboard-teacher mr-2"></i>View Course</b-button>
                                        <b-button v-if="certificate.type === 'registration' && certificate.conference" type="is-primary" outlined size="is-small" tag="router-link" :to="'/conference/' + certificate.registrationId"><i class="fas fa-presentation mr-2"></i>View Conference</b-button>
                                        <b-button type="is-primary" size="is-small" tag="router-link" :to="applicationRoute(certificate)"><i :class="['fas', stateMeta(certificate).ctaIcon, 'mr-2']"></i>{{ stateMeta(certificate).cta }}</b-button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Completed Certificates (/reports/completed): certification > year
                         accordions (shared archive pattern with Course Enrollments / Conference Records) -->
                    <div v-else-if="currentView === 'completed'">

                        <empty-state v-if="completedCertificates.length === 0" icon="fa-info-circle" text="You have no completed certificates yet."/>
                        <div v-else class="mt-5">
                            <year-tabs :years="certificateYears" :value="activeCertificateYear.key" @input="certificateYear = $event"/>
                            <empty-state v-if="activeCertificateYear.groups.length === 0" icon="fa-info-circle" :text="'No certificates earned in ' + activeCertificateYear.label + '.'"/>
                            <template v-for="(group, groupIndex) in activeCertificateYear.groups">
                                <!-- Later certification headers get extra room so they don't crowd the previous group's last card -->
                                <section-header :key="'gh-' + group.key" :title="group.title" icon="fa-file-certificate" :count="group.items.length" :class="['mb-4', groupIndex === 0 ? 'mt-2' : 'mt-6 pt-3']"/>

                                <!-- WIAA certification status is managed by FinalForms -->
                                <div v-if="group.key === 2" class="content has-text-white mb-6 pb-2">
                                    <div class="mb-4"><span class="has-text-weight-bold">WIAA</span> certifications are managed by <span class="has-text-weight-bold">FinalForms</span>. To update your certification status in FinalForms, log in to FinalForms and click 'Sync Certifications with my247ed' in your profile. If your certifications are not syncing properly please check your
                                        <router-link to="account" class="has-text-white has-text-underline">integration settings</router-link>
                                        and your WIAA Membership ID. See <a href="https://app.my247ed.com/#/wiaa-help" target="_blank" class="has-text-white has-text-underline">this page</a> for additional information and help.
                                    </div>
                                    <a href="https://wiaa.finalforms.com/athletic_association_staff/find_district" target="_blank">
                                        <b-button type="is-light" outlined size="is-small">View Certification Status at <span class="has-text-weight-bold">FinalForms</span><i class="far fa-up-right-from-square ml-3"></i></b-button>
                                    </a>
                                </div>

                                    <div v-for="certificate in group.items" :key="`${certificate.type}-${certificate.id}`" class="info-box is-card mb-4">
                                        <div class="box-main">
                                            <div class="box-icon is-hidden-touch"><i class="fad fa-certificate fa-2x has-text-success"></i></div>
                                            <div class="box-body">
                                                <div class="card-title">{{ certTitle(certificate) }}</div>
                                                <div class="card-meta" v-if="certificate.certification">{{ certificate.certification.title }}</div>
                                                <!-- No "Approved" chip: the section + the approved-on line already say it -->
                                                <div class="card-chips mt-2" v-if="certificate.totalUnits || nonPdfAwards(certificate).length">
                                                    <status-chip v-if="certificate.totalUnits" :label="certificate.totalUnits + ' ' + unitTitle(certificate)" type="info" icon="fa-clock"/>
                                                    <!-- Non-PDF awards are recognitions, not downloads -->
                                                    <template v-for="award in nonPdfAwards(certificate)">
                                                        <status-chip :key="award.id" :label="award.title" :type="award.type === 'CERT' ? 'success' : 'neutral'" :icon="award.type === 'CERT' ? 'fa-certificate' : 'fa-award'"/>
                                                    </template>
                                                </div>
                                                <div class="card-meta mt-3">
                                                    <div v-if="certificate.course"><span class="meta-label">Course</span>{{ certificate.course.title }}</div>
                                                    <div v-if="certificate.conference"><span class="meta-label">Conference</span>{{ certificate.conference.title }}</div>
                                                    <div v-if="certificate.application.organization"><span class="meta-label">Provider</span>{{ certificate.application.organization.title }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="box-actions">
                                            <div class="box-lead box-info"><i class="far fa-fw fa-calendar-check mr-2"></i>Approved {{ certificate.completedAt | moment('MMMM Do, YYYY') }}</div>
                                            <div class="box-buttons">
                                                <b-button v-if="certificate.type === 'enrollment' && certificate.course" type="is-primary" outlined size="is-small" tag="router-link" :to="'/course/' + certificate.enrollmentId"><i class="fas fa-chalkboard-teacher mr-2"></i>View Course</b-button>
                                                <b-button v-if="certificate.type === 'registration' && certificate.conference" type="is-primary" outlined size="is-small" tag="router-link" :to="'/conference/' + certificate.registrationId"><i class="fas fa-presentation mr-2"></i>View Conference</b-button>
                                                <b-button type="is-primary" outlined size="is-small" tag="router-link" :to="applicationRoute(certificate)"><i class="fas fa-file-certificate mr-2"></i>View Application</b-button>
                                                <!-- PDF awards are the real deliverable: one download button each -->
                                                <b-button v-for="award in pdfAwards(certificate)" :key="'pdf-' + award.id" tag="a" target="_blank" type="is-primary" size="is-small" :href="getPDFUrl(certificate, award)"><i class="fas fa-download mr-2"></i>{{ award.title }}</b-button>
                                            </div>
                                        </div>
                                    </div>
                            </template>
                        </div>

                    </div>

                    <!-- Receipts (/reports/receipts): every Stripe payment on the account,
                         one line each with what it paid for, followed by any itemized
                         documents published from the portal's Finance > Receipts & Invoices tool -->
                    <div v-else-if="currentView === 'receipts'">

                        <empty-state v-if="receipts.length === 0 && billingDocuments.length === 0" icon="fa-info-circle" text="No payment receipts or invoices are on your account yet."/>
                        <template v-else>
                            <div class="mt-5" v-if="receipts.length">
                                <div v-for="receipt in receipts" :key="'receipt-' + receipt.id" class="info-box is-card mb-4">
                                    <div class="box-main">
                                        <div class="box-icon is-hidden-touch"><i :class="['fad', 'fa-2x', receiptIcon(receipt), receipt.status === 'refunded' ? 'has-text-grey' : 'has-text-info']"></i></div>
                                        <div class="box-body">
                                            <div class="card-title">{{ receipt.label }}</div>
                                            <div class="card-chips mt-2" v-if="receipt.status === 'refunded'">
                                                <status-chip label="Refunded" type="neutral" icon="fa-rotate-left"/>
                                            </div>
                                            <div class="card-meta mt-2">
                                                <div v-for="(item, index) in receipt.items" :key="'item-' + index">
                                                    <span class="meta-label">{{ item.kind === 'conference' ? 'Conference' : 'Course' }}</span>
                                                    <router-link v-if="item.link && item.id" :to="itemRoute(item)">{{ item.title }}</router-link>
                                                    <span v-else>{{ item.title }}</span>
                                                    <span v-if="item.note" class="has-text-grey"> &middot; {{ item.note }}</span>
                                                </div>
                                                <div v-if="receipt.detail"><span class="meta-label">Application</span>{{ receipt.detail }}</div>
                                                <div>
                                                    <span class="meta-label">Total</span>{{ receipt.total_amount | currency }}
                                                    <span v-if="receipt.tax_amount > 0" class="has-text-grey"> &middot; includes {{ receipt.tax_amount | currency }} sales tax</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="box-actions">
                                        <div class="box-lead box-info">
                                            <i class="far fa-fw fa-calendar mr-2"></i>Paid {{ receipt.date | moment('MMMM Do, YYYY') }}
                                            <span v-if="receipt.refunded_at"> &middot; refunded {{ receipt.refunded_at | moment('MMMM Do, YYYY') }}</span>
                                        </div>
                                        <div class="box-buttons">
                                            <b-button v-if="receipt.receipt_available" type="is-primary" size="is-small" @click="viewReceipt(receipt)" :loading="receiptLoading === receipt.id"><i class="fad fa-file-invoice-dollar mr-2"></i>View Receipt</b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Same breathing room as the h2 stack on the Account page -->
                            <div :class="receipts.length ? 'mt-6 pt-5' : 'mt-5'" v-if="billingDocuments.length">
                                <section-header v-if="receipts.length" title="Itemized Receipts & Invoices" icon="fa-file-invoice-dollar" class="mb-5"/>
                                <div v-for="doc in billingDocuments" :key="'billing-' + doc.id" class="info-box is-card mb-4">
                                    <div class="box-main">
                                        <div class="box-icon is-hidden-touch"><i class="fad fa-file-invoice-dollar fa-2x has-text-info"></i></div>
                                        <div class="box-body">
                                            <div class="card-title">{{ billingDocTitle(doc) }}</div>
                                            <div class="card-meta mt-2"><span class="meta-label">Total</span>{{ billingDocTotal(doc) }}</div>
                                        </div>
                                    </div>
                                    <div class="box-actions">
                                        <div class="box-lead box-info"><i class="far fa-fw fa-calendar mr-2"></i>{{ billingDocDate(doc) }}</div>
                                        <div class="box-buttons">
                                            <b-button tag="a" target="_blank" type="is-primary" size="is-small" :href="billingDocPdfUrl(doc)"><i class="fas fa-download mr-2"></i>Download PDF</b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p class="support-note mt-4">Need an itemized receipt or invoice that isn't listed here? Contact <a href="mailto:support@my247ed.com">support@my247ed.com</a>.</p>
                        </template>
                    </div>

                    <!-- Bottom padding for better scrolling -->
                    <div style="height: 6rem;"></div>

                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import YearTabs from "../../components/framework/YearTabs";
    import SectionHeader from "../../components/framework/SectionHeader";
    import {bucketByYear, defaultYearKey} from "../../utils/yearBuckets";
    import EmptyState from "../../components/framework/EmptyState";
    import StatusChip from "../../components/framework/StatusChip";
    import {mapActions, mapGetters} from "vuex";
    import {client as http, apiUrl} from "../../http_client";

    // Student-facing presentation per application state (the API derives
    // `state` with the same rule the dashboard attention feed uses). Order
    // doubles as the in-progress sort: things needing the student first,
    // things waiting on us last.
    const STATE_META = {
        changes_requested: {order: 0, chip: 'Changes requested', chipType: 'warning', chipIcon: 'fa-pen-to-square', icon: 'fa-file-pen', iconClass: 'has-text-warning', cta: 'Revise Application', ctaIcon: 'fa-pen-to-square', note: 'The reviewer asked for changes. Update the application and resubmit it.'},
        // unpaid = approved by the reviewer, the certification fee is what's left
        unpaid: {order: 1, chip: 'Payment due', chipType: 'warning', chipIcon: 'fa-credit-card', icon: 'fa-file-invoice-dollar', iconClass: 'has-text-warning', cta: 'Pay Fee', ctaIcon: 'fa-credit-card', note: 'Your application has been approved. Pay the certification fee to receive your award.'},
        rejected: {order: 2, chip: 'Not approved', chipType: 'danger', chipIcon: 'fa-file-xmark', icon: 'fa-file-xmark', iconClass: 'has-text-danger', cta: 'Review Application', ctaIcon: 'fa-eye', note: 'This application was not approved.'},
        in_progress: {order: 3, chip: 'In progress', chipType: 'neutral', chipIcon: 'fa-hourglass-half', icon: 'fa-hourglass-half', iconClass: 'has-text-warning', cta: 'Continue Application', ctaIcon: 'fa-arrow-right', note: null},
        in_review: {order: 4, chip: 'In review', chipType: 'info', chipIcon: 'fa-magnifying-glass', icon: 'fa-file-magnifying-glass', iconClass: 'has-text-info', cta: 'View Application', ctaIcon: 'fa-eye', note: 'Submitted and awaiting review. Nothing else is needed from you right now.'},
    };

    // Available Applications: what stands between the student and applying,
    // keyed by the API's `status` (users.controller getAvailableApplications)
    // plus the client-side `cert_off` (certification switched off). `ready`
    // items (every apply gate passes) lead the page under "Ready to Apply"; the
    // rest — conference attendance requirements included, since those are
    // only checked on the conference page — are listed under "Not Ready Yet".
    const AVAILABLE_META = {
        ready: {ready: true, order: 0, chip: 'Ready to Apply', chipType: 'success', chipIcon: 'fa-circle-check', iconClass: 'has-text-success'},
        requirements: {order: 1, chip: 'Requirements Apply', chipType: 'info', chipIcon: 'fa-list-check', iconClass: 'has-text-grey-light'},
        minutes: {order: 2, chip: 'More Time Needed', chipType: 'warning', chipIcon: 'fa-clock', iconClass: 'has-text-warning'},
        coursework: {order: 3, chip: 'Finish Coursework', chipType: 'neutral', chipIcon: 'fa-list-ol', iconClass: 'has-text-grey-light'},
        milestone: {order: 4, chip: 'Reflection In Review', chipType: 'info', chipIcon: 'fa-flag-swallowtail', iconClass: 'has-text-grey-light'},
        setup: {order: 5, chip: 'Study Plan Needed', chipType: 'warning', chipIcon: 'fa-clipboard-list', iconClass: 'has-text-grey-light'},
        payment: {order: 6, chip: 'Payment Needed', chipType: 'warning', chipIcon: 'fa-credit-card', iconClass: 'has-text-grey-light'},
        cert_off: {order: 7, chip: 'Certification Off', chipType: 'neutral', chipIcon: 'fa-toggle-off', iconClass: 'has-text-grey-light'},
        expired: {order: 8, chip: 'Enrollment Expired', chipType: 'danger', chipIcon: 'fa-calendar-xmark', iconClass: 'has-text-grey-light'},
    };

    export default {
        components: {YearTabs, SectionHeader, EmptyState, StatusChip, Framework, SidePanelNav, MainHeader},
        filters: {
            lowercase(v) {
                return (v || '').toLowerCase();
            }
        },
        props: {
            // Route param: 'available' | 'applications' | 'completed' | 'receipts'. The bare
            // path ('') is a landing that picks a view once the data is in
            // (see landOnBestView).
            view: {type: String, default: ''}
        },
        data: function() {
            return {
                certificateYear: null,
                receipts: [],
                billingDocuments: [],
                receiptLoading: null,
                availableLoading: true,
                loading: true,
                apiUrl
            };
        },
        computed: {
            currentView() {
                return ['available', 'completed', 'receipts'].includes(this.view) ? this.view : 'progress';
            },
            viewCount() {
                if (this.currentView === 'available') return this.availableLoading ? null : (this.availableApplications.length || null);
                if (this.loading) return null;
                return {progress: this.inProgressCertificates.length, completed: this.completedCertificates.length, receipts: this.receipts.length + this.billingDocuments.length}[this.currentView] || null;
            },
            viewTitle() {
                return {available: 'Available Applications', progress: 'In Progress Applications', completed: 'Completed Certificates', receipts: 'Receipts'}[this.currentView];
            },
            ...mapGetters(['currentUser', 'certificates', 'certificateCertifications', 'certificationOn', 'certificationById']),
            // Shared with the side menu's "N Ready to Apply" tag (store module certificates)
            availableApplications() {
                return this.$store.getters.availableApplications || [];
            },
            // Ready first (by status, then newest course/conference — the API's
            // order), then everything still waiting on something
            availableSections() {
                const sorted = this.availableApplications
                    .map((item, index) => ({item, index}))
                    .sort((a, b) => (this.availableMeta(a.item).order - this.availableMeta(b.item).order) || (a.index - b.index))
                    .map(entry => entry.item);
                return [
                    {key: 'ready', title: 'Ready to Apply', icon: 'fa-circle-check', items: sorted.filter(item => this.availableMeta(item).ready)},
                    {key: 'waiting', title: 'Not Ready Yet', icon: 'fa-hourglass-half', items: sorted.filter(item => !this.availableMeta(item).ready)},
                ].filter(section => section.items.length);
            },
            certifications() {
                return this.certificateCertifications;
            },
            completedCertificates() {
                return (this.certificates || []).filter(cert => cert.status === 'completed');
            },
            // Needs-you-first, then most recently touched
            inProgressCertificates() {
                return (this.certificates || [])
                    .filter(cert => cert.status === 'in_progress')
                    .slice()
                    .sort((a, b) => (this.stateMeta(a).order - this.stateMeta(b).order)
                        || (new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)));
            },
            // Completed certificates grouped certification > year for the
            // archive accordions (same shape as Enrollments.vue). A lone
            // group opens by default; several start closed.
            // Earned certificates by approval year (newest first, undated last);
            // inside a year, by certification with "Other" last
            certificateYears() {
                return bucketByYear(this.completedCertificates, {
                    date: c => c.completedAt,
                    groupKey: c => c.certification?.id || 'other',
                    groupTitle: c => c.certification?.title || 'Other Certificates'
                });
            },
            activeCertificateYear() {
                const years = this.certificateYears;
                return years.find(y => y.key === this.certificateYear) || years.find(y => y.key === defaultYearKey(years)) || {groups: [], label: ''};
            },
        },
        methods: {
            // An application for a certification the student switched off waits
            // on the switch, whatever its course-side status
            availableStatus(item) {
                return item.certification && !this.certificationOn(item.certification.id) ? 'cert_off' : item.status;
            },
            availableMeta(item) {
                return AVAILABLE_META[this.availableStatus(item)] || AVAILABLE_META.coursework;
            },
            availableNote(item) {
                const kind = item.application.type === 'form' ? 'form' : 'application';
                switch (this.availableStatus(item)) {
                    case 'requirements': return "This conference sets attendance requirements for this application. Check its applications page to see whether you've met them.";
                    case 'minutes': return `Record at least ${item.minMinutesRequired} minutes of study time to apply (${item.recordedMinutes || 0} recorded so far).`;
                    case 'coursework': return item.lessons && item.lessons.total
                        ? `Finish all lessons in this course to apply (${item.lessons.completed} of ${item.lessons.total} done).`
                        : 'Finish all lessons in this course to apply.';
                    case 'milestone': return 'A milestone reflection is awaiting approval. It must be approved before you can apply.';
                    case 'setup': return 'Your study plan must be submitted and accepted before you can apply.';
                    case 'payment': return 'This enrollment has a payment that needs attention before you can apply.';
                    case 'expired': return 'This enrollment has expired. Visit the course to explore reactivation options.';
                    case 'cert_off': {
                        const certification = this.certificationById(item.certification.id);
                        return `Available with ${(certification && certification.certification && certification.certification.title) || item.certification.title}. Turn it on under Your Account to use this ${kind}.`;
                    }
                    default: return null;
                }
            },
            availableRoute(item) {
                return item.type === 'enrollment'
                    ? {name: 'course-applications', params: {enrollmentId: item.enrollmentId}}
                    : {name: 'conf-applications', params: {registrationId: item.registrationId}};
            },
            loadAvailableApplications() {
                this.availableLoading = true;
                // Always refetch on visit: the menu may hold a list from earlier in the session
                this.$store.dispatch('getAvailableApplications').catch(err => {
                    console.error('Failed to load available applications:', err);
                }).finally(() => {
                    this.availableLoading = false;
                });
            },
            stateMeta(certificate) {
                return STATE_META[certificate.state] || STATE_META.in_progress;
            },
            certTitle(certificate) {
                return certificate.certificationCert && certificate.certificationCert.title ? certificate.certificationCert.title : certificate.application.title;
            },
            unitTitle(certificate) {
                return certificate.certification && certificate.certification.unit_title ? certificate.certification.unit_title : 'Units';
            },
            pdfAwards(certificate) {
                return (certificate.awards || []).filter(a => a.type === 'PDF');
            },
            nonPdfAwards(certificate) {
                return (certificate.awards || []).filter(a => a.type !== 'PDF');
            },
            applicationRoute(certificate) {
                return certificate.type === 'enrollment'
                    ? {name: 'course-application', params: {enrollmentId: certificate.enrollmentId, applicationId: certificate.id}}
                    : {name: 'application', params: {registrationId: certificate.registrationId, applicationId: certificate.id}};
            },
            ...mapActions(['getCertificates']),
            loadCertificates() {
                this.loading = true;
                this.getCertificates().then(() => {
                    this.loading = false;
                    this.landOnBestView();
                }).catch(err => {
                    console.error('Failed to load certificates:', err);
                    this.loading = false;
                });
            },
            // Bare /reports (top nav, old bookmarks): an empty "In Progress
            // Applications" page is a poor landing for the many students who
            // have none, so send them to their certificates instead. The nav
            // item itself links to /reports/applications explicitly.
            landOnBestView() {
                if (this.view !== '' || this.$route.name !== 'reports') return;
                const view = this.inProgressCertificates.length ? 'applications' : 'completed';
                this.$router.replace({name: 'reports', params: {view}}).catch(() => {});
            },
            loadReceipts() {
                // Every Stripe payment on the account (courses, bundles,
                // reactivations, conferences, application fees), newest first.
                // Non-blocking, same as the billing documents.
                http.get('app/receipts', {force: true}).then(response => {
                    this.receipts = response.data.receipts || [];
                }).catch(err => {
                    console.error('Failed to load receipts:', err);
                });
            },
            receiptIcon(receipt) {
                return {registration: 'fa-presentation', conf_application: 'fa-file-certificate', course_application: 'fa-file-certificate', reactivation: 'fa-rotate-right', package: 'fa-boxes-stacked'}[receipt.type] || 'fa-file-invoice-dollar';
            },
            itemRoute(item) {
                return (item.kind === 'conference' ? '/conference/' : '/course/') + item.id;
            },
            viewReceipt(receipt) {
                this.receiptLoading = receipt.id;
                http.get(`app/receipts/${receipt.id}/url`, {force: true}).then(response => {
                    this.receiptLoading = null;
                    if (response.data.url) window.open(response.data.url, '_blank');
                }).catch(err => {
                    this.receiptLoading = null;
                    this.$buefy.dialog.alert({
                        title: 'Receipt Unavailable',
                        message: (err.response && err.response.data && err.response.data.error) || 'No receipt is available for this payment.',
                        type: 'is-warning',
                        confirmText: 'OK'
                    });
                });
            },
            loadBillingDocuments() {
                // Itemized receipts/invoices published from the portal's billing tool.
                // Non-blocking — the page renders fine while (or if) this fails.
                http.get('app/billing-documents', {force: true}).then(response => {
                    this.billingDocuments = response.data.documents || [];
                }).catch(err => {
                    console.error('Failed to load billing documents:', err);
                });
            },
            billingDocTitle(doc) {
                const label = doc.type === 'receipt' ? 'Receipt' : 'Invoice';
                return doc.title || `${label} ${doc.number || '#' + doc.id}`;
            },
            billingDocDate(doc) {
                // document_date is a bare date — format in UTC so it can't slip a day
                return doc.document_date
                    ? new Date(doc.document_date).toLocaleDateString('en-US', {timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric'})
                    : '';
            },
            billingDocTotal(doc) {
                const total = Number(doc.total) || 0;
                return (total < 0 ? '-$' : '$') + Math.abs(total).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            },
            billingDocPdfUrl(doc) {
                // Same new-tab auth pattern as the printable study log PDF
                return `${this.apiUrl}app/billing-documents/${doc.id}/pdf?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`;
            },
            getPDFUrl(certificate, award) {
                if (certificate.type === 'enrollment') {
                    return `${this.apiUrl}app/enrollments/${certificate.enrollmentId}/applications/${certificate.id}/pdf/${award.key}`;
                } else if (certificate.type === 'registration') {
                    return `${this.apiUrl}app/registrations/${certificate.registrationId}/applications/${certificate.id}/pdf/${award.key}`;
                }
                return '#';
            },
        },
        mounted() {
            this.loadCertificates();
            this.loadAvailableApplications();
            this.loadReceipts();
            this.loadBillingDocuments();
        }
    };
</script>

<style lang="scss" scoped>
    .has-text-underline {
        text-decoration: underline;
    }

    // Card layout comes from the global .info-box.is-card (assets/scss/_cta.scss)
    .card-title {
        font-weight: 700;
        line-height: 1.3;
    }

    .card-meta {
        font-size: 0.85rem;
        color: #7a7a7a;
    }

    .card-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }

    // "Course  Collaborating with Colleagues" — a quiet label gives the
    // meta lines their context (matches the application page's labels)
    .review-message {
        margin-top: 0.3rem;
        font-style: italic;
        font-weight: 400;
        white-space: pre-line;
    }

    .meta-label {
        display: inline-block;
        min-width: 6.5rem;
        padding-right: 0.5rem;
        font-weight: 600;
        color: #9a9a9a;
    }

    // Sits on the page's navy ground, below the cards
    .support-note {
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.75);

        a {
            color: #fff;
            text-decoration: underline;
        }
    }
</style>
